import type {
  CancelBookingFlowStep,
  CancelBookingFormValues,
  CancelBookingNewMainBooker,
} from '@/scopes/account/booking-details/pages/components/CancelBookingAction/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import { useBookingDetailsStore } from '@/scopes/account/booking-details/infrastructure/useBookingDetailsStore'
import styles from '@/scopes/account/booking-details/pages/components/CancelBookingAction/CancelBookingForm.module.css'
import {
  getCancelBookingBodyBackButtonConfig,
  getCancelBookingSheetLabels,
} from '@/scopes/account/booking-details/pages/components/CancelBookingAction/cancelBookingOptionRegistry'
import { CancelBookingStepContent } from '@/scopes/account/booking-details/pages/components/CancelBookingAction/cancelBookingStepRenderer'
import { CancelBookingSubmitResult } from '@/scopes/account/booking-details/pages/components/CancelBookingAction/steps/CancelBookingSubmitResult'
import { FullCancellationErrorResult } from '@/scopes/account/booking-details/pages/components/CancelBookingAction/steps/FullCancellationErrorResult'
import { RebookingSheetContent } from '@/scopes/account/booking-details/pages/components/ManageBookingSection/RebookingSheetContent'
import { getFullCancellationVariant, getIsCancelBookingMainBookerSelected } from '@/scopes/account/booking-details/pages/selectors'

interface CancelBookingFormProps {
  open: boolean
  onClose: () => void
}

export function CancelBookingForm({ open, onClose }: CancelBookingFormProps) {
  const { formatMessage } = useIntl()
  const {
    partialCancellationFeeRequested,
    partialCancellationRequested,
    fullCancellationCostsRequested,
    cancelBookingConfirmed,
    voucherCancellationRequested,
    rebookingButtonClicked,
  } = useBookingDetailsActions()
  const fullCancellationVariant = useBookingDetailsStore(getFullCancellationVariant)
  const submitStatus = useBookingDetailsStore(state => state.cancelBookingSubmitStatus)
  const fullCancellationCostsStatus = useBookingDetailsStore(state => state.fullCancellationCosts.status)
  const isSubmitting = submitStatus === 'loading' || fullCancellationCostsStatus === 'loading'
  const [step, setStep] = useState<CancelBookingFlowStep>('select-option')

  const methods = useForm<CancelBookingFormValues>({
    mode: 'onBlur',
    shouldUnregister: false,
    defaultValues: {
      option: '',
      participantIds: [],
      roomLayoutPreference: '',
      newMainBooker: { name: '', email: '' },
      confirmPartialCancellationTerms: false,
      requestPriceQuote: false,
    },
  })

  const { control, reset, trigger, getValues } = methods
  const participantIds = useWatch({ control, name: 'participantIds' })
  const isMainBookerSelected = useBookingDetailsStore(
    state => getIsCancelBookingMainBookerSelected(state, participantIds),
  )

  useEffect(() => {
    if (!open) {
      reset()
      setStep('select-option')
    }
  }, [open, reset])

  function handleBack() {
    if (step === 'select-option') {
      onClose()
      return
    }

    setStep('select-option')
  }

  function handleGoToPreviousStep() {
    const bodyBackButtonConfig = getCancelBookingBodyBackButtonConfig(step)
    if (bodyBackButtonConfig)
      setStep(bodyBackButtonConfig.previousStep)
  }

  async function handleContinue() {
    if (isSubmitting)
      return

    if (step === 'select-option') {
      const option = getValues('option')
      if (!option)
        return

      if (option === 'exchange') {
        void rebookingButtonClicked()
        setStep('exchange')
        return
      }

      if (option === 'full-cancellation') {
        void fullCancellationCostsRequested()
        setStep('full-cancellation-confirmation')
        return
      }

      if (option === 'voucher') {
        setStep('voucher-cancellation-confirmation')
        return
      }

      setStep(option)
      return
    }

    if (step === 'voucher-cancellation-confirmation') {
      await voucherCancellationRequested()
      return
    }

    if (step === 'full-cancellation-confirmation') {
      await cancelBookingConfirmed()
      return
    }

    if (step === 'partial-cancellation') {
      if (!(await trigger('participantIds')))
        return

      void partialCancellationFeeRequested()
      setStep('partial-cancellation-fee')
      return
    }

    if (step === 'partial-cancellation-fee') {
      const fieldsToValidate: (
        | keyof CancelBookingFormValues
        | `newMainBooker.${keyof CancelBookingNewMainBooker}`
      )[] = ['confirmPartialCancellationTerms']

      if (isMainBookerSelected) {
        fieldsToValidate.push('newMainBooker.name', 'newMainBooker.email')
      }

      if (!(await trigger(fieldsToValidate)))
        return

      await partialCancellationRequested(getValues())
    }
  }

  const { titleKey, cancelButtonLabelKey, confirmButtonLabelKey } = getCancelBookingSheetLabels(
    step,
    fullCancellationVariant,
    isSubmitting,
  )
  const isFullCancellationCostsLoading = step === 'full-cancellation-confirmation'
    && fullCancellationCostsStatus === 'loading'
  const isVoucherSubmitting = step === 'voucher-cancellation-confirmation' && submitStatus === 'loading'
  const showFooter = (submitStatus === 'idle' || isVoucherSubmitting)
    && !isFullCancellationCostsLoading
    && step !== 'exchange'
    && !(step === 'full-cancellation-confirmation' && fullCancellationCostsStatus === 'error')
  const showLoaderOverlay = isSubmitting && !isVoucherSubmitting

  if (submitStatus === 'success' || submitStatus === 'error') {
    let submitResult = null

    if (step === 'full-cancellation-confirmation' && fullCancellationVariant) {
      submitResult = (
        <CancelBookingSubmitResult
          status={submitStatus}
          step="full-cancellation-confirmation"
          fullCancellationVariant={fullCancellationVariant}
        />
      )
    }
    else if (step !== 'full-cancellation-confirmation') {
      submitResult = (
        <CancelBookingSubmitResult
          status={submitStatus}
          step={step}
        />
      )
    }

    return (
      <>
        <Sheet.Header
          closeLabel={formatMessage({ id: 'close' })}
        />
        <Sheet.Content>
          {submitResult}
        </Sheet.Content>
      </>
    )
  }

  if (step === 'full-cancellation-confirmation' && fullCancellationCostsStatus === 'error') {
    return (
      <>
        <Sheet.Header closeLabel={formatMessage({ id: 'close' })} />
        <Sheet.Content>
          <FullCancellationErrorResult />
        </Sheet.Content>
      </>
    )
  }

  if (isFullCancellationCostsLoading) {
    return (
      <>
        <Sheet.Header
          closeLabel={formatMessage({ id: 'close' })}
          titleLabel={titleKey ? formatMessage({ id: titleKey }) : undefined}
        />
        <Sheet.Content className={styles.loadingSheetContent}>
          <IndeterminateProgressCircle />
        </Sheet.Content>
      </>
    )
  }

  if (step === 'exchange') {
    return (
      <>
        <Sheet.Header
          closeLabel={formatMessage({ id: 'close' })}
          titleLabel={formatMessage({ id: titleKey })}
        />
        <Sheet.Content>
          <RebookingSheetContent />
        </Sheet.Content>
      </>
    )
  }

  return (
    <FormProvider {...methods}>
      <Sheet.Header
        closeLabel={formatMessage({ id: 'close' })}
        titleLabel={titleKey ? formatMessage({ id: titleKey }) : undefined}
      />
      <Sheet.Content>
        <div className={styles.formWrapper}>
          <div className={showLoaderOverlay ? styles.formDisabled : undefined}>
            <CancelBookingStepContent
              step={step}
              onGoToPreviousStep={handleGoToPreviousStep}
            />
          </div>
          {showLoaderOverlay && (
            <div className={styles.loaderOverlay}>
              <IndeterminateProgressCircle />
            </div>
          )}
        </div>
      </Sheet.Content>
      {showFooter && (
        <Sheet.Footer>
          <div className={styles.actions}>
            <Button
              variant="primary"
              onClick={handleContinue}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {formatMessage({ id: confirmButtonLabelKey })}
            </Button>
            <Button
              variant="transparentNeutral"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              {formatMessage({ id: cancelButtonLabelKey })}
            </Button>
          </div>
        </Sheet.Footer>
      )}
    </FormProvider>
  )
}
